import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FeedSection } from "@/components/community/FeedSection";
import { ChallengesSection } from "@/components/community/ChallengesSection";
import { ChallengeCelebration } from "@/components/community/ChallengeCelebration";
import { PremiumTeaser } from "@/components/community/PremiumTeaser";
import { useChallengeCelebration } from "@/hooks/useChallengeCelebration";
import { useLanguage } from "@/i18n/LanguageContext";
import { fadeUp } from "@/lib/motion";

const tabTriggerClass =
  "rounded-lg py-2 text-sm font-medium text-[hsl(var(--muted-foreground))] transition-colors data-[state=active]:bg-cyan-300/90 data-[state=active]:text-slate-950 data-[state=active]:shadow-none";

export default function CommunityPage() {
  const { t } = useLanguage();
  const location = useLocation();
  // Stage 2.2: allow deep-linking straight to the Challenges tab
  const [tab, setTab] = useState(
    location.state?.tab === "challenges" ? "challenges" : "feed"
  );
  // Stage 2.2: once-only challenge completion celebration
  const { celebration, dismiss } = useChallengeCelebration();

  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);
  }, [location.state]);

  return (
    <div className="space-y-4">
      <motion.header {...fadeUp(0)} className="pt-2">
        <p className="text-xs uppercase tracking-[0.22em] text-cyan-300/70 font-medium">
          {t("community.kicker")}
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t("community.title")}
        </h1>
      </motion.header>

      <motion.div {...fadeUp(0.06)}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 h-auto rounded-xl bg-white/5 border border-white/10 p-1">
            <TabsTrigger value="feed" data-testid="community-tab-feed" className={tabTriggerClass}>
              {t("community.tabFeed")}
            </TabsTrigger>
            <TabsTrigger
              value="challenges"
              data-testid="community-tab-challenges"
              className={tabTriggerClass}
            >
              {t("community.tabChallenges")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="feed" className="mt-4">
            <FeedSection />
          </TabsContent>
          <TabsContent value="challenges" className="mt-4">
            <ChallengesSection />
          </TabsContent>
        </Tabs>
      </motion.div>

      <motion.div {...fadeUp(0.12)}>
        <PremiumTeaser />
      </motion.div>

      {/* Stage 2.2: once-only challenge completion celebration */}
      <ChallengeCelebration celebration={celebration} onDismiss={dismiss} />
    </div>
  );
}
